import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createArticleApi,
  createAnonymizedArticleApi,
  updateArticleApi,
  patchArticleApi,
  deleteArticleApi,
  deleteArticlesApi,
  createSharepointApi,
  updateSharepointApi,
  deleteSharepointApi,
  createFeedbackFormApi,
  submitFeedbackApi,
  submitFeedbackViaFormApi,
  exportArticlesApi,
  undoImportArticlesApi,
  createCategoryApi,
  createPropertyApi,
  createCategoryPropertyOrSupplier,
} from '../api/articleApi'
import { type Article, type Sharepoint, type FeedbackForm, type FeedbackSubmission } from '../utils/types'
import { type Category, type Property, type Supplier } from '../../Supplier/utils/types'
import LogTool from '../../../logger/logTools'
import { articleKeys, sharepointKeys, categoryKeys, propertyKeys } from '../utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

const log = new LogTool({ context: 'ArticleMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new article
 */
export function useCreateArticle(): UseMutationResult<Article, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createArticleApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.lists() })
      log.info('Article created successfully')
    },
    onError: (error) => {
      log.error('Error creating article:', error)
    },
  })
}

/**
 * Hook to create a new anonymized request article
 */
export function useCreateAnonymizedArticle(): UseMutationResult<Article, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createAnonymizedArticleApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.lists() })
      log.info('Anonymized article created successfully')
    },
    onError: (error) => {
      log.error('Error creating anonymized article:', error)
    },
  })
}

/**
 * Hook to update an article (full update with PUT)
 */
export function useUpdateArticle(): UseMutationResult<
  Article,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateArticleApi(idOrSelf, data),
    onSuccess: async (updatedArticle) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: articleKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Article) =>
              item.key === updatedArticle.key ? updatedArticle : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (category, properties, files, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: articleKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({
        queryKey: articleKeys.detail(Number(getPKfromSelf(updatedArticle.self)))
      })
      log.info('Article updated successfully')
    },
    onError: (error) => {
      log.error('Error updating article:', error)
    },
  })
}

/**
 * Hook to patch an article (partial update with PATCH)
 */
export function usePatchArticle(): UseMutationResult<
  Article,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => patchArticleApi(idOrSelf, data),
    onSuccess: async (updatedArticle) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: articleKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Article) =>
              item.key === updatedArticle.key ? updatedArticle : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (category, properties, files, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: articleKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({
        queryKey: articleKeys.detail(Number(getPKfromSelf(updatedArticle.self)))
      })
      log.info('Article patched successfully')
    },
    onError: (error) => {
      log.error('Error patching article:', error)
    },
  })
}

/**
 * Hook to delete a single article
 */
export function useDeleteArticle(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteArticleApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.lists() })
      log.info('Article deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting article:', error)
    },
  })
}

/**
 * Hook to delete multiple articles
 */
export function useDeleteArticles(): UseMutationResult<void, Error, Article[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteArticlesApi,
    onSuccess: () => {
      // Don't invalidate here - let handleDeleteArticle invalidate after clearing selection
      // This prevents SmartTable from showing warnings about selected entries that no longer exist
      log.info('Articles deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting articles:', error)
    },
  })
}

/**
 * Hook to create a sharepoint
 */
export function useCreateSharepoint(): UseMutationResult<Sharepoint, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createSharepointApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      log.info('Sharepoint created successfully')
    },
    onError: (error) => {
      log.error('Error creating sharepoint:', error)
    },
  })
}

/**
 * Hook to update a sharepoint
 */
export function useUpdateSharepoint(): UseMutationResult<
  Sharepoint,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateSharepointApi(idOrSelf, data),
    onSuccess: async (updatedSharepoint) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: sharepointKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Sharepoint) =>
              item.key === updatedSharepoint.key ? updatedSharepoint : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data
      await queryClient.invalidateQueries({ 
        queryKey: sharepointKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({
        queryKey: sharepointKeys.detail(Number(getPKfromSelf(updatedSharepoint.self)))
      })
      log.info('Sharepoint updated successfully')
    },
    onError: (error) => {
      log.error('Error updating sharepoint:', error)
    },
  })
}

/**
 * Hook to delete a sharepoint
 */
export function useDeleteSharepoint(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSharepointApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      log.info('Sharepoint deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting sharepoint:', error)
    },
  })
}

/**
 * Hook to create a feedback form
 */
export function useCreateFeedbackForm(): UseMutationResult<FeedbackForm, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createFeedbackFormApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feedbackForms'] })
      log.info('Feedback form created successfully')
    },
    onError: (error) => {
      log.error('Error creating feedback form:', error)
    },
  })
}

/**
 * Hook to submit feedback
 */
export function useSubmitFeedback(): UseMutationResult<FeedbackSubmission, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: submitFeedbackApi,
    onSuccess: async (_, variables) => {
      // Invalidate feedback submissions for the sharepoint
      if (variables.sharepoint) {
        await queryClient.invalidateQueries({ queryKey: ['feedbackSubmissions', variables.sharepoint] })
      }
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      log.info('Feedback submitted successfully')
    },
    onError: (error) => {
      log.error('Error submitting feedback:', error)
    },
  })
}

/**
 * Hook to submit feedback via feedback form
 */
export function useSubmitFeedbackViaForm(): UseMutationResult<
  any,
  Error,
  { feedbackFormUrl: string; submissionData: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ feedbackFormUrl, submissionData }) => 
      submitFeedbackViaFormApi(feedbackFormUrl, submissionData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: ['feedbackForms'] })
      log.info('Feedback submitted via form successfully')
    },
    onError: (error) => {
      log.error('Error submitting feedback via form:', error)
    },
  })
}

/**
 * Hook to export articles
 */
export function useExportArticles(): UseMutationResult<{ url: string }, Error, void> {
  return useMutation({
    mutationFn: exportArticlesApi,
    onSuccess: () => {
      log.info('Articles exported successfully')
    },
    onError: (error) => {
      log.error('Error exporting articles:', error)
    },
  })
}

/**
 * Hook to undo article import
 */
export function useUndoImportArticles(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: undoImportArticlesApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.lists() })
      log.info('Article import undone successfully')
    },
    onError: (error) => {
      log.error('Error undoing article import:', error)
    },
  })
}

/**
 * Hook to create a new category
 */
export function useCreateCategory(): UseMutationResult<Category, Error, { name: string }> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createCategoryApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: categoryKeys.all })
      log.info('Category created successfully')
    },
    onError: (error) => {
      log.error('Error creating category:', error)
    },
  })
}

/**
 * Hook to create a new property
 */
export function useCreateProperty(): UseMutationResult<Property, Error, { name: string; value?: string; type?: string }> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createPropertyApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: propertyKeys.all })
      log.info('Property created successfully')
    },
    onError: (error) => {
      log.error('Error creating property:', error)
    },
  })
}

/**
 * Hook to create a category, property, or supplier
 * This is a unified hook that replaces the old handleAddOption callback-based function
 */
export function useCreateCategoryOrProperty(): UseMutationResult<
  Category | Property | Supplier,
  Error,
  { url: 'v1/supplier/suppliers/' | 'v1/items/categories/' | 'v1/items/properties/'; input: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ url, input }) => createCategoryPropertyOrSupplier(url, input),
    onSuccess: async (result, variables) => {
      // Invalidate relevant queries based on what was created
      switch (variables.url) {
        case 'v1/items/categories/':
          await queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
          await queryClient.invalidateQueries({ queryKey: categoryKeys.all })
          break
        case 'v1/items/properties/':
          await queryClient.invalidateQueries({ queryKey: propertyKeys.lists() })
          await queryClient.invalidateQueries({ queryKey: propertyKeys.all })
          break
        case 'v1/supplier/suppliers/':
          // Suppliers have their own query keys - invalidate if needed
          await queryClient.invalidateQueries({ queryKey: ['suppliers'] })
          break
      }
      log.info('Category/Property/Supplier created successfully')
    },
    onError: (error) => {
      log.error('Error creating category/property/supplier:', error)
    },
  })
}
